import React = require('react');
import { ColorService } from '@tableau/vizportalclient-common-components';
import { RadioButtonWithLabelGroupWidget, RadioLayoutType } from '@tableau/widgets';
import { Formik, FormikProps } from 'formik';

import { HelpService } from '~commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { FormikCheckbox } from '~src/commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { Messages } from '../../codegen/Localize';
import { UserSettings } from '../services/resources/UserSettings';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { AccessibilitySettingsProps } from './AccessibilitySettings';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';

const DEEPLINK_ID = 'viewer-controlled-theme-setting';
const VIEWER_CONTROLLED_THEME_FORMIK_NAME = 'ViewerControlledTheme';
const VIEWER_CONTROLLED_VIZ_THEME_FORMIK_NAME = 'ViewerControlledVizTheme';
export const VIEWER_CONTROLLED_THEME_TEST_ID = 'viewer-controlled-theme-setting';

// Dark chrome (ViewerControlledTheme.Dark == 1); gates the viz-area checkbox.
const DARK_CHROME_VALUE = '1';

// Help token for the toast "Learn more" link; passed verbatim to getHelpUrlByCategory.
const VIZ_THEME_HELP_TOKEN = 'cloud-appearance-theme';

// Ids that link each setting's description text to its control via aria-describedby, so screen-reader
// users hear the explanation when focus lands on the chrome radio group / viz-area checkbox.
const CHROME_THEME_DESCRIPTION_ID = 'viewer-controlled-theme-description';
const VIZ_THEME_DESCRIPTION_ID = 'viewer-controlled-viz-theme-description';

export interface ViewerControlledThemeField {
    [VIEWER_CONTROLLED_THEME_FORMIK_NAME]: string;
    // Binary viz-area theme: true == Dark (viz theme option 1), false == Default.
    [VIEWER_CONTROLLED_VIZ_THEME_FORMIK_NAME]: boolean;
}

type ViewerControlledThemeOption = 0 | 1 | 2;
type ViewerControlledVizThemeOption = 0 | 1;

function showButtons(formik: FormikProps<ViewerControlledThemeField>, currentTheme: number, currentVizTheme: number) {
    const selectedTheme = Number(formik.values[VIEWER_CONTROLLED_THEME_FORMIK_NAME]);
    const selectedVizTheme = formik.values[VIEWER_CONTROLLED_VIZ_THEME_FORMIK_NAME] ? 1 : 0;
    const themeUnchanged = selectedTheme === currentTheme;
    const vizThemeUnchanged = !FeatureFlags.viewerControlledThemes_VizAreaEnabled || selectedVizTheme === currentVizTheme;
    if (!formik.touched || !formik.isValid || formik.isSubmitting || (themeUnchanged && vizThemeUnchanged)) {
        return false;
    }
    return true;
}

export function ViewerControlledThemeSetting(props: AccessibilitySettingsProps): JSX.Element {
    const [currentTheme, setCurrentTheme] = React.useState<ViewerControlledThemeOption>(0);
    const [currentVizTheme, setCurrentVizTheme] = React.useState<ViewerControlledVizThemeOption>(0);
    // Id of the current switch-warning toast, so it can be dismissed before re-popping.
    const lastWarningToastId = React.useRef<number | null>(null);

    const VIEWER_CONTROLLED_THEME_OPTIONS = [
        { label: Messages.userSettings_viewerControlledTheme_defaultLight(), value: '0', testId: 'viewer-controlled-theme-light' },
        { label: Messages.userSettings_viewerControlledTheme_dark(), value: '1', testId: 'viewer-controlled-theme-dark' },
        { label: Messages.userSettings_viewerControlledTheme_highContrast(), value: '2', testId: 'viewer-controlled-theme-high-contrast' }
    ].filter(option => option.value !== '2' || FeatureFlags.viewerControlledThemes_HighContrastEnabled);

    // Close the switch-warning toast if showing (warnings do not auto-dismiss) and clear its id.
    function dismissSwitchWarning() {
        if (lastWarningToastId.current !== null) {
            ToasterService.instance.close(lastWarningToastId.current);
            lastWarningToastId.current = null;
        }
    }

    // On opt-in, pop the switch warning (dismissing any prior one first so they do not stack).
    function handleVizThemeChange(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        dismissSwitchWarning();
        if (event.target.checked) {
            lastWarningToastId.current = ToasterService.instance.pop({
                type: ToastType.Warning,
                text: Messages.userSettings_viewerControlledVizTheme_switchWarning(),
                actionMessage: Messages.learnMore(),
                actionHref: HelpService.getHelpUrlByCategory(VIZ_THEME_HELP_TOKEN),
                testId: VIEWER_CONTROLLED_THEME_TEST_ID
            });
        }
        return Promise.resolve();
    }

    // Set the chrome theme; leaving Dark makes the switch warning stale, so dismiss it.
    function handleChromeThemeChange(formik: FormikProps<ViewerControlledThemeField>, value: string) {
        if (value !== DARK_CHROME_VALUE) {
            dismissSwitchWarning();
        }
        formik.setFieldValue(VIEWER_CONTROLLED_THEME_FORMIK_NAME, value);
    }

    async function handleSubmit(values: ViewerControlledThemeField) {
        dismissSwitchWarning();

        const newTheme = Number(values[VIEWER_CONTROLLED_THEME_FORMIK_NAME]) as ViewerControlledThemeOption;
        // Persist the viewer's raw intent; the monolith runtime gates dark viz to dark chrome at render time.
        const newVizTheme: ViewerControlledVizThemeOption = values[VIEWER_CONTROLLED_VIZ_THEME_FORMIK_NAME] ? 1 : 0;

        try {
            await UserSettings.updateViewerControlledTheme(newTheme);
            setCurrentTheme(newTheme);
            ColorService.instance.setTheme(newTheme === 1 ? 'tds-dark' : 'tds-light');
            if (FeatureFlags.viewerControlledThemes_VizAreaEnabled) {
                await UserSettings.updateViewerControlledVizTheme(newVizTheme);
                setCurrentVizTheme(newVizTheme);
            }
            await getSessionManagementServiceInstance().refreshSessionInfo().catch(() => {});
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess(), testId: VIEWER_CONTROLLED_THEME_TEST_ID });
        } catch {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
        }
    }

    React.useEffect(
        () => {
            UserSettings.getViewerControlledTheme()
                .then((result: number) => setCurrentTheme(result as ViewerControlledThemeOption))
                .catch(() => setCurrentTheme(0));

            if (FeatureFlags.viewerControlledThemes_VizAreaEnabled) {
                UserSettings.getViewerControlledVizTheme()
                    .then((result: number) => setCurrentVizTheme(result as ViewerControlledVizThemeOption))
                    .catch(() => setCurrentVizTheme(0));
            }
        },
        []
    );

    return !FeatureFlags.viewerControlledThemes_ViewingModeEnabled ? null : (
        <Formik
            initialValues={{
                ViewerControlledTheme: currentTheme.toString(),
                ViewerControlledVizTheme: currentVizTheme === 1
            }}
            onSubmit={(values: ViewerControlledThemeField) => handleSubmit(values)}
            enableReinitialize={true}
        >
            {
                (formik: FormikProps<ViewerControlledThemeField>) => (
                    <DeepLink deepLinkId={DEEPLINK_ID}>
                        <form onSubmit={formik.handleSubmit}>
                            <div style={{ display: 'flex' }}>
                                <div data-tb-test-id={`userSettings-${VIEWER_CONTROLLED_THEME_TEST_ID}`}>
                                    <div className={UserSettingsStyle.settingsLabelText}>
                                        {Messages.userSettings_viewerControlledTheme_label()}
                                    </div>
                                </div>
                                <div className={UserSettingsStyle.settingsLabelDescription}>
                                    <div className={UserSettingsStyle.detailsValue} id={CHROME_THEME_DESCRIPTION_ID}>
                                        {Messages.userSettings_viewerControlledTheme_description()}
                                    </div>
                                    <div className={UserSettingsStyle.detailsValue}>
                                        <RadioButtonWithLabelGroupWidget
                                            label=""
                                            aria-describedby={CHROME_THEME_DESCRIPTION_ID}
                                            buttonList={VIEWER_CONTROLLED_THEME_OPTIONS}
                                            checkedIndex={VIEWER_CONTROLLED_THEME_OPTIONS.findIndex(option => option.value === formik.values[VIEWER_CONTROLLED_THEME_FORMIK_NAME])}
                                            handleChange={(value: string) => handleChromeThemeChange(formik, value)}
                                            layout={RadioLayoutType.Horizontal}
                                        />
                                    </div>
                                    {FeatureFlags.viewerControlledThemes_VizAreaEnabled &&
                                        <div className={UserSettingsStyle.detailsValueSmallPadding}>
                                            <div className={UserSettingsStyle.detailsValue} id={VIZ_THEME_DESCRIPTION_ID}>
                                                {Messages.userSettings_viewerControlledVizTheme_description()}
                                            </div>
                                            <FormikCheckbox
                                                containerStyle={{ marginLeft: 0 }}
                                                name={VIEWER_CONTROLLED_VIZ_THEME_FORMIK_NAME}
                                                label={Messages.userSettings_viewerControlledVizTheme_checkboxLabel()}
                                                testId='viewer-controlled-viz-theme-checkbox'
                                                aria-describedby={VIZ_THEME_DESCRIPTION_ID}
                                                beforeChange={handleVizThemeChange}
                                                // Only actionable under Dark chrome; disabled (value preserved) otherwise.
                                                disabled={formik.values[VIEWER_CONTROLLED_THEME_FORMIK_NAME] !== DARK_CHROME_VALUE}
                                            />
                                        </div>
                                    }
                                    {
                                        showButtons(formik, currentTheme, currentVizTheme) &&
                                        <InlineCancelAndSaveButtons
                                            testId={VIEWER_CONTROLLED_THEME_TEST_ID}
                                            formik={formik}
                                            // Cancel abandons the edit, so dismiss any lingering switch warning
                                            // (warnings do not auto-dismiss) before resetting the form fields.
                                            handleClick={() => { dismissSwitchWarning(); formik.resetForm(); }}
                                        />
                                    }
                                </div>
                            </div>
                        </form>
                    </DeepLink>
                )
            }
        </Formik>
    );
}

export const ViewerControlledThemeSettingBeaconConfig = beaconComponentRegistryEntry({
    command: () => Messages.userSettings_viewerControlledTheme_label(),
    deeplinkId: DEEPLINK_ID,
    url: (props) => getUserAccountSettingsURL(props.sessionInfo),
    tags: () => { return [Messages.userSettings_viewerControlledTheme_label(), Messages.userSettings_Accessibility_Header()]; },
    isAdmin: false,
});
